import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import HaspObject from './HaspObject';

const HANDLE_RADIUS = 5;
const HIT_STROKE_WIDTH = 12;

export default class HaspLine extends HaspObject {
    _relPts = [];          // points relative to group's top-left (bounding box origin)
    _selectedPointIdx = -1;
    _isSelected = false;
    _keyHandler = null;
    needsLiveDragRedraw = false;

    // ─── Bounds computation ────────────────────────────────────────────────────

    static _computeBounds(absPoints) {
        const xs = absPoints.filter((_, i) => i % 2 === 0);
        const ys = absPoints.filter((_, i) => i % 2 === 1);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        return {
            x: minX,
            y: minY,
            width: Math.max(Math.max(...xs) - minX, 1),
            height: Math.max(Math.max(...ys) - minY, 1),
        };
    }

    // ─── Constructor ───────────────────────────────────────────────────────────

    constructor(config) {
        // Accept both OpenHASP pair format [[x,y],[x,y]] and internal flat [x,y,x,y].
        // Pair format means points came from an OpenHASP JSON import; flat means they
        // were captured directly from canvas drawing and are already in canvas coords.
        let absPoints = config.points;
        const isOpenHaspFormat = Array.isArray(absPoints?.[0]);

        if (isOpenHaspFormat) {
            absPoints = absPoints.flat();
        }
        if (!absPoints || absPoints.length < 4) {
            const bx = typeof config.x === 'number' ? config.x : 0;
            const by = typeof config.y === 'number' ? config.y : 0;
            absPoints = [bx, by, bx + 100, by + 100];
        }
        absPoints = absPoints.map(Number);

        // OpenHASP line points are relative to the object's (x,y) position.
        // With y_invert=1, y=0 is the bottom of the bounding box (increasing upward).
        // Convert to absolute canvas coords (y from top, downward):
        //   canvas_abs_x = obj_x + openhasp_rel_x
        //   canvas_abs_y = obj_y + (max_openhasp_y - openhasp_rel_y)
        const yInvert = typeof config.y_invert === 'number' ? config.y_invert : 1;
        if (isOpenHaspFormat) {
            const bx = typeof config.x === 'number' ? config.x : 0;
            const by = typeof config.y === 'number' ? config.y : 0;
            if (yInvert === 1) {
                const maxY = Math.max(...absPoints.filter((_, i) => i % 2 === 1));
                absPoints = absPoints.map((v, i) =>
                    i % 2 === 0 ? v + bx : by + (maxY - v)
                );
            } else {
                absPoints = absPoints.map((v, i) => i % 2 === 0 ? v + bx : v + by);
            }
        }

        const bounds = HaspLine._computeBounds(absPoints);
        config.x = bounds.x;
        config.y = bounds.y;
        config.width = bounds.width;
        config.height = bounds.height;

        super(config);

        // Relative points (origin = group's top-left = bounding box min)
        this._relPts = absPoints.map((v, i) =>
            i % 2 === 0 ? v - bounds.x : v - bounds.y
        );

        // Main line shape
        const mainStyle = this.style('main');
        this.lineShape = new Konva.Line({
            id: uuidv4(),
            points: [...this._relPts],
            stroke: mainStyle.line_color || '#ffffff',
            strokeWidth: Number(mainStyle.line_width) || 1,
            lineCap: mainStyle.line_rounded ? 'round' : 'butt',
            lineJoin: mainStyle.line_rounded ? 'round' : 'miter',
            hitStrokeWidth: HIT_STROKE_WIDTH,
            listening: true,
            name: 'line-shape',
        });
        this.add(this.lineShape);

        // Hide the infrastructure rect – still used by getBounds() / snapping
        this.rect.opacity(0);
        this.rect.listening(false);
        this.rect.fill('transparent');
        this.rect.strokeWidth(0);

        // Dashed bounding-box shown while selected
        this.selectionBox = new Konva.Rect({
            id: uuidv4(),
            x: 0,
            y: 0,
            width: bounds.width,
            height: bounds.height,
            fill: 'transparent',
            stroke: '#3B82F6',
            strokeWidth: 1,
            dash: [4, 4],
            listening: false,
            visible: false,
        });
        this.add(this.selectionBox);

        // Container for draggable point handles
        this.handlesGroup = new Konva.Group({ listening: true, visible: false });
        this.add(this.handlesGroup);
        this._rebuildHandles();

        // Property change → visual update
        this.onStyle('main', 'line_color', () => {
            this.lineShape.stroke(this.style('main').line_color || '#ffffff');
        });
        this.onStyle('main', 'line_width', () => {
            this.lineShape.strokeWidth(Number(this.style('main').line_width) || 1);
        });
        this.onStyle('main', 'line_rounded', () => {
            const rounded = this.style('main').line_rounded;
            this.lineShape.lineCap(rounded ? 'round' : 'butt');
            this.lineShape.lineJoin(rounded ? 'round' : 'miter');
        });

        this.idTextObj.moveToTop();
    }

    // ─── Geometry helpers ──────────────────────────────────────────────────────

    _absolutePoints() {
        const ox = this.x();
        const oy = this.y();
        return this._relPts.map((v, i) => i % 2 === 0 ? v + ox : v + oy);
    }

    // Recalculate the bounding box from current absolute points, reposition the
    // group to the new origin and update all dependent visual elements.
    _updateGeometry() {
        const absPoints = this._absolutePoints();
        const bounds = HaspLine._computeBounds(absPoints);

        this.x(bounds.x);
        this.y(bounds.y);
        this.size({ width: bounds.width, height: bounds.height });
        this.rect.size({ width: bounds.width, height: bounds.height });
        this.selectionBox.position({ x: 0, y: 0 });
        this.selectionBox.size({ width: bounds.width, height: bounds.height });

        // Recompute relative points with the new origin
        this._relPts = absPoints.map((v, i) =>
            i % 2 === 0 ? v - bounds.x : v - bounds.y
        );
        this.lineShape.points([...this._relPts]);
        this._rebuildHandles();
        this.idTextObj.moveToTop();
    }

    // ─── Point handles ─────────────────────────────────────────────────────────

    _rebuildHandles() {
        this.handlesGroup.destroyChildren();

        for (let i = 0; i < this._relPts.length; i += 2) {
            const pIdx = i / 2;
            const isSelected = pIdx === this._selectedPointIdx;

            const handle = new Konva.Circle({
                id: uuidv4(),
                x: this._relPts[i],
                y: this._relPts[i + 1],
                radius: HANDLE_RADIUS,
                fill: isSelected ? '#F97316' : '#FFFFFF',
                stroke: '#3B82F6',
                strokeWidth: 1.5,
                draggable: true,
                listening: true,
                name: 'point-handle',
            });

            handle.on('dragstart', () => {
                handle.dragBoundFunc((absPos) => {
                    return this._snapAbsPos(absPos, pIdx);
                });
            });

            handle.on('dragmove', () => {
                this._relPts[pIdx * 2] = handle.x();
                this._relPts[pIdx * 2 + 1] = handle.y();
                this.lineShape.points([...this._relPts]);

                // Update the selection box live in group-local coords so it always
                // encloses all points even when a handle is dragged outside the
                // current group bounds (which only resets on dragend).
                const xs = this._relPts.filter((_, i) => i % 2 === 0);
                const ys = this._relPts.filter((_, i) => i % 2 === 1);
                const minX = Math.min(...xs), maxX = Math.max(...xs);
                const minY = Math.min(...ys), maxY = Math.max(...ys);
                this.selectionBox.x(minX);
                this.selectionBox.y(minY);
                this.selectionBox.width(Math.max(maxX - minX, 1));
                this.selectionBox.height(Math.max(maxY - minY, 1));

                if (this.getLayer()) this.getLayer().requestRender();
            });

            handle.on('dragend', () => {
                handle.dragBoundFunc(undefined);
                const stage = this.getStage();
                if (stage && stage.snapLayer) {
                    stage.snapLayer.clearGuides();
                    stage.snapLayer.flushGuides();
                }
                this._updateGeometry();
                if (this.getLayer()) {
                    this.getLayer().layerCallback('transform-end', this);
                    this.getLayer().requestRender();
                }
            });

            handle.on('mousedown', (e) => {
                e.cancelBubble = true;
                this._selectPoint(pIdx);
            });

            this.handlesGroup.add(handle);
        }
    }

    _selectPoint(idx) {
        this._selectedPointIdx = idx;
        // Update handle colors in place — do NOT call _rebuildHandles() here because
        // _selectPoint is called from a handle's mousedown handler, and destroying
        // the handle while it is the active mousedown target prevents Konva from
        // starting the drag on it.
        this.handlesGroup.getChildren().forEach((h, i) => {
            h.fill(i === idx ? '#F97316' : '#FFFFFF');
        });
        if (this.getLayer()) {
            this.getLayer().layerCallback('point-selected', this);
            this.getLayer().requestRender();
        }
    }

    // ─── Snapping helper ───────────────────────────────────────────────────────

    // Convert absPos (screen-absolute from dragBoundFunc) → stage-content →
    // snap → back to screen-absolute.
    // excludeIdx: index of the handle being dragged (its own point is excluded).
    // Snapping to sibling objects/guidelines draws red guides (default).
    // Snapping to another handle on this same line draws yellow guides.
    _snapAbsPos(absPos, excludeIdx = -1) {
        const stage = this.getStage();
        if (!stage) return absPos;
        const sx = stage.scaleX(), sy = stage.scaleY();
        const sp = stage.position();
        const origCx = (absPos.x - sp.x) / sx;
        const origCy = (absPos.y - sp.y) / sy;

        // ── Object / guideline snap (red) ──────────────────────────────────
        let cx = origCx, cy = origCy;
        let objR = null;
        if (stage.snapManager) {
            const targets = stage.snapManager.collectSnapTargets(this, stage.guidelineManager);
            objR = stage.snapManager.findSnapForContentPos(
                { x: origCx, y: origCy }, { width: 0, height: 0 }, targets
            );
            cx = objR.snapX;
            cy = objR.snapY;
        }

        // ── Own-handle snap (yellow) ───────────────────────────────────────
        const SNAP_DIST = 8;
        const ox = this.x(), oy = this.y();
        let ownGuideX, ownGuideY;
        let ownSnapX = origCx, ownSnapY = origCy;
        let ownMatchY = origCy, ownMatchX = origCx;

        for (let i = 0; i < this._relPts.length; i += 2) {
            if (i / 2 === excludeIdx) continue;
            const hx = this._relPts[i] + ox;
            const hy = this._relPts[i + 1] + oy;
            if (Math.abs(origCx - hx) <= SNAP_DIST) {
                ownGuideX = hx;
                ownSnapX  = hx;
                ownMatchY = hy;
            }
            if (Math.abs(origCy - hy) <= SNAP_DIST) {
                ownGuideY = hy;
                ownSnapY  = hy;
                ownMatchX = hx;
            }
        }

        // ── Segment midpoint snap (yellow perpendicular) ──────────────────
        // Always checked — takes priority over own-handle snap (2D proximity wins over 1D axis)
        const useOwn = ownGuideX !== undefined || ownGuideY !== undefined;
        // Triggers when cursor is within SNAP_DIST of the perpendicular line through the midpoint.
        let midSnap = null;
        for (let i = 0; i < this._relPts.length - 2; i += 2) {
            const pi = i / 2;
            if (pi === excludeIdx || pi + 1 === excludeIdx) continue;
            const ax = this._relPts[i]     + ox, ay = this._relPts[i + 1] + oy;
            const bx = this._relPts[i + 2] + ox, by = this._relPts[i + 3] + oy;
            const mx = (ax + bx) / 2, my = (ay + by) / 2;
            const segLen = Math.hypot(bx - ax, by - ay);
            if (segLen === 0) continue;
            const tx = (bx - ax) / segLen, ty = (by - ay) / segLen;
            const cmx = origCx - mx, cmy = origCy - my;
            const alongSeg = cmx * tx + cmy * ty;
            const d = Math.abs(alongSeg);
            if (d < SNAP_DIST && (!midSnap || d < midSnap.dist)) {
                midSnap = {
                    snapX: origCx - alongSeg * tx,
                    snapY: origCy - alongSeg * ty,
                    midX: mx, midY: my,
                    segDx: bx - ax, segDy: by - ay, dist: d,
                };
            }
        }

        // ── Resolve final position (priority: midpoint > own-handle > object) ──
        let finalX, finalY;
        if (midSnap) {
            finalX = midSnap.snapX;
            finalY = midSnap.snapY;
        } else if (useOwn) {
            finalX = ownGuideX !== undefined ? ownSnapX : cx;
            finalY = ownGuideY !== undefined ? ownSnapY : cy;
        } else {
            finalX = cx;
            finalY = cy;
        }

        // ── Draw guides ────────────────────────────────────────────────────
        if (stage.snapLayer) {
            stage.snapLayer.clearGuides();
            if (midSnap) {
                stage.snapLayer.drawPerpendicularGuide(
                    midSnap.midX, midSnap.midY, midSnap.segDx, midSnap.segDy,
                    midSnap.snapX, midSnap.snapY, '#FFFF00'
                );
            }
            if (useOwn) {
                if (ownGuideX !== undefined)
                    stage.snapLayer.drawVerticalGuide(
                        ownGuideX,
                        Math.min(origCy, ownMatchY), Math.max(origCy, ownMatchY),
                        '#FFFF00'
                    );
                if (ownGuideY !== undefined)
                    stage.snapLayer.drawHorizontalGuide(
                        ownGuideY,
                        Math.min(origCx, ownMatchX), Math.max(origCx, ownMatchX),
                        '#FFFF00'
                    );
            } else if (!midSnap && objR) {
                if (objR.guideX !== undefined)
                    stage.snapLayer.drawVerticalGuide(objR.guideX, objR.guideXyMin, objR.guideXyMax);
                if (objR.guideY !== undefined)
                    stage.snapLayer.drawHorizontalGuide(objR.guideY, objR.guideYxMin, objR.guideYxMax);
            }
            stage.snapLayer.flushGuides();
        }

        return { x: finalX * sx + sp.x, y: finalY * sy + sp.y };
    }

    // ─── Selection state ───────────────────────────────────────────────────────

    setSelected(selected) {
        this._isSelected = selected;
        this.handlesGroup.visible(selected);
        this.selectionBox.visible(selected);

        if (!selected) {
            const stage = this.getStage();
            if (stage && stage.snapLayer) {
                stage.snapLayer.clearGuides();
                stage.snapLayer.flushGuides();
            }
        }

        if (selected) {
            this._keyHandler = (e) => {
                if (e.key === 'Delete' && this._selectedPointIdx >= 0) {
                    // Need at least 2 points
                    if (this._relPts.length > 4) {
                        e.preventDefault();
                        e.stopPropagation();
                        this._relPts.splice(this._selectedPointIdx * 2, 2);
                        this._selectedPointIdx = -1;
                        this._updateGeometry();
                        if (this.getLayer()) {
                            this.getLayer().layerCallback('point-deleted', this);
                            this.getLayer().requestRender();
                        }
                    }
                }
            };
            document.addEventListener('keydown', this._keyHandler, true);
        } else {
            if (this._keyHandler) {
                document.removeEventListener('keydown', this._keyHandler, true);
                this._keyHandler = null;
            }
            this._selectedPointIdx = -1;
            this._rebuildHandles();
        }

        if (this.getLayer()) this.getLayer().requestRender();
    }

    // ─── Drag / transform overrides ────────────────────────────────────────────

    transformStart() {
        // No special behavior needed; handles manage their own snapping
    }

    transformEnd() {
        // Called after group drag ends; handles stay in their correct relative
        // positions so only a handle rebuild is needed.
        this._rebuildHandles();
        this.idTextObj.moveToTop();
    }

    // ─── Bounds / snap points ──────────────────────────────────────────────────

    getBounds() {
        const stage = this.getStage();
        return stage
            ? this.rect.getClientRect({ relativeTo: stage })
            : this.rect.getClientRect();
    }

    getSnapPoints() {
        const box = this.getBounds();
        return {
            vertical:   [box.x, box.x + box.width  / 2, box.x + box.width],
            horizontal: [box.y, box.y + box.height / 2, box.y + box.height],
        };
    }

    // ─── Segment insertion ─────────────────────────────────────────────────────

    // Find which segment is closest to (cx, cy) in stage-content coords
    // Returns { segmentIndex, t, ix, iy } where segmentIndex is the index of
    // the start point of the segment, t is the interpolation param, and
    // ix/iy is the closest point on the segment.
    _closestSegment(cx, cy) {
        const ox = this.x(), oy = this.y();
        let bestDist = Infinity, bestSeg = -1, bestT = 0, bestIx = 0, bestIy = 0;

        for (let i = 0; i < this._relPts.length - 2; i += 2) {
            const ax = this._relPts[i]     + ox;
            const ay = this._relPts[i + 1] + oy;
            const bx = this._relPts[i + 2] + ox;
            const by = this._relPts[i + 3] + oy;

            const dx = bx - ax, dy = by - ay;
            const lenSq = dx * dx + dy * dy;
            if (lenSq === 0) continue;

            const t = Math.max(0, Math.min(1, ((cx - ax) * dx + (cy - ay) * dy) / lenSq));
            const ix = ax + t * dx;
            const iy = ay + t * dy;
            const dist = Math.hypot(cx - ix, cy - iy);

            if (dist < bestDist) {
                bestDist = dist;
                bestSeg = i / 2;
                bestT = t;
                bestIx = ix;
                bestIy = iy;
            }
        }
        return { segmentIndex: bestSeg, t: bestT, dist: bestDist, ix: bestIx, iy: bestIy };
    }

    // Insert a new point between segmentIndex and segmentIndex+1
    insertPoint(segmentIndex, absX, absY) {
        const ox = this.x(), oy = this.y();
        const relX = absX - ox;
        const relY = absY - oy;
        this._relPts.splice((segmentIndex + 1) * 2, 0, relX, relY);
        this._selectedPointIdx = segmentIndex + 1;
        this._updateGeometry();
        if (this.getLayer()) {
            this.getLayer().layerCallback('point-inserted', this);
            this.getLayer().requestRender();
        }
    }

    // ─── Export ────────────────────────────────────────────────────────────────

    objectExport(_page, objectData) {
        const yInvert = this.y_invert ?? 1;
        const h = Math.round(this.height());
        const pairs = [];

        // Export points relative to the object's bounding-box origin.
        // With y_invert=1, flip so y=0 is the bottom: openhasp_y = h - rel_y
        for (let i = 0; i < this._relPts.length; i += 2) {
            const rx = Math.round(this._relPts[i]);
            const ry = Math.round(this._relPts[i + 1]);
            pairs.push([rx, yInvert === 1 ? h - ry : ry]);
        }

        objectData.points    = pairs;
        objectData.auto_size = this.auto_size ?? 0;
        objectData.y_invert  = yInvert;
        // x, y, w, h are kept — they encode the line's position on the page
    }

    // ─── Cleanup ───────────────────────────────────────────────────────────────

    destroy() {
        if (this._keyHandler) {
            document.removeEventListener('keydown', this._keyHandler, true);
            this._keyHandler = null;
        }
        super.destroy();
    }
}
